import React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Link } from 'react-router-dom';
import { FaExclamationCircle } from 'react-icons/fa';
import Modal from '../common/Modal';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/modals.css';
import './LoginModal.css';

const LoginModal = ({ isOpen, onClose, onSwitchToRegister, onLoginSuccess, onForgotPassword, isStandalonePage = false }) => {
  const { login } = useAuth();

  if (!isOpen) return null;

  const handleSubmit = async (values, { setSubmitting, setStatus }) => {
    try {
      const isEmail = values.identifier.includes('@');
      const credentials = {
        password: values.password
      };
      
      if (isEmail) {
        credentials.email = values.identifier;
      } else {
        credentials.username = values.identifier;
      }
      
      const userData = await login(credentials);
      
      // Handle successful login
      if (onLoginSuccess) {
        onLoginSuccess(userData);
      }
      
      // Close modal if not in standalone page mode
      if (!isStandalonePage) {
        onClose();
      }
    } catch (error) {
      setStatus({ error: error.message || 'Failed to login. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  // Modal backdrop click is handled by the Modal component

  // If using standalone page mode, render with page layout instead of modal
  if (isStandalonePage) {
    return (
      <div className="auth-page-container">
        <div className="auth-page-modal">
          <div className="auth-page-content">
            <h2>Login</h2>
            
            <LoginForm 
              onSubmit={handleSubmit} 
              onSwitchToRegister={onSwitchToRegister}
              onForgotPassword={onForgotPassword} 
            />
          </div>
        </div>
      </div>
    );
  }

  // Otherwise use the Modal component
  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="Login"
      size="medium"
      closeOnBackdrop={false}
      closeOnEsc={false}
    >
        
      <LoginForm 
        onSubmit={handleSubmit} 
        onSwitchToRegister={onSwitchToRegister} 
        onForgotPassword={onForgotPassword}
      />
    </Modal>
  );
};

// Extract form to separate component for reuse
const LoginForm = ({ onSubmit, onSwitchToRegister, onForgotPassword }) => {
  const initialValues = {
    identifier: '',
    password: ''
  };

  const validationSchema = Yup.object({
    identifier: Yup.string().required('Username or Email is required'),
    password: Yup.string().required('Password is required')
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ isSubmitting, status, errors, touched }) => (
        <Form>
          {status && status.error && (
            <div className="alert alert-danger">{status.error}</div>
          )}
        
          <div className="form-group">
            <label htmlFor="identifier">Username or Email</label>
            <Field
              type="text"
              name="identifier"
              id="identifier"
              className={`form-control ${errors.identifier && touched.identifier ? 'form-control-error' : ''}`}
            />
            <ErrorMessage name="identifier">
              {msg => (
                <div className="inline-error-message">
                  <FaExclamationCircle className="error-icon" />
                  <span>{msg}</span>
                </div>
              )}
            </ErrorMessage>
          </div>
        
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <Field
              type="password"
              name="password"
              id="password"
              className={`form-control ${errors.password && touched.password ? 'form-control-error' : ''}`}
            />
            <ErrorMessage name="password">
              {msg => (
                <div className="inline-error-message">
                  <FaExclamationCircle className="error-icon" />
                  <span>{msg}</span>
                </div>
              )}
            </ErrorMessage>
          </div>
        
          <button type="submit" disabled={isSubmitting} className="btn btn-primary">
            {isSubmitting ? 'Logging in...' : 'Login'}
          </button>
        
          <div className="form-footer">
            <p>
              {onForgotPassword ? (
                <button
                  type="button"
                  className="link-button"
                  onClick={onForgotPassword}
                >
                  Forgot Password?
                </button>
              ) : (
                <Link to="/forgot-password" className="link-text">
                  Forgot Password?
                </Link>
              )}
            </p>
            <p>
              Don't have an account?{' '}
              {onSwitchToRegister ? (
                <button 
                  type="button" 
                  className="link-button" 
                  onClick={onSwitchToRegister}
                >
                  Register
                </button>
              ) : (
                <Link to="/register">Register</Link>
              )}
            </p>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default LoginModal;